const DECIMAL_PATTERN = /^[+-]?(?:\d+|\d+[.,]\d+|[.,]\d+)$/

export const MAX_RATIO_INPUT_LENGTH = 256
export const RESULT_DECIMAL_PLACES = 10

export type RatioMode = 'solve' | 'simplify' | 'scale'
export type RatioFieldKey = 'a' | 'b' | 'c' | 'multiplier'
export type RatioCalculationError =
  | 'invalid_number'
  | 'number_too_long'
  | 'division_by_zero'
  | 'undefined_ratio'
  | 'zero_multiplier'

export interface RatioInputs {
  a: string
  b: string
  c: string
  multiplier: string
}

interface DecimalValue {
  coefficient: bigint
  scale: number
  normalized: string
  canonical: string
}

export interface RatioCalculationResult {
  display: string
  formula: string
  detail: string
  rounded: boolean
}

export interface RatioCalculationState {
  status: 'incomplete' | 'error' | 'ready'
  error: RatioCalculationError | null
  invalidFields: RatioFieldKey[]
  result: RatioCalculationResult | null
  snapshot: string | null
}

function absolute(value: bigint): bigint {
  return value < 0n ? -value : value
}

function greatestCommonDivisor(left: bigint, right: bigint): bigint {
  let a = absolute(left)
  let b = absolute(right)

  while (b !== 0n) {
    const remainder = a % b
    a = b
    b = remainder
  }

  return a
}

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(exponent)
}

function canonicalDecimal(coefficient: bigint, scale: number): string {
  if (coefficient === 0n) return '0'

  let canonicalCoefficient = coefficient
  let canonicalScale = scale
  while (canonicalScale > 0 && canonicalCoefficient % 10n === 0n) {
    canonicalCoefficient /= 10n
    canonicalScale -= 1
  }

  const negative = canonicalCoefficient < 0n
  const digits = absolute(canonicalCoefficient).toString().padStart(canonicalScale + 1, '0')
  if (canonicalScale === 0) return `${negative ? '-' : ''}${digits}`

  const splitAt = digits.length - canonicalScale
  return `${negative ? '-' : ''}${digits.slice(0, splitAt)}.${digits.slice(splitAt)}`
}

export function parseDecimal(value: string): DecimalValue | null {
  if (value.length > MAX_RATIO_INPUT_LENGTH) return null

  const trimmed = value.trim()
  if (!DECIMAL_PATTERN.test(trimmed)) return null

  const normalizedSeparator = trimmed.replace(',', '.')
  const negative = normalizedSeparator.startsWith('-')
  const unsigned = normalizedSeparator.replace(/^[+-]/, '')
  const [rawInteger = '0', rawFraction = ''] = unsigned.split('.')
  const integer = rawInteger || '0'
  const digits = `${integer}${rawFraction}`.replace(/^0+(?=\d)/, '') || '0'
  const coefficient = BigInt(`${negative ? '-' : ''}${digits}`)
  const normalizedMagnitude = rawFraction
    ? `${BigInt(integer).toString()}.${rawFraction}`
    : BigInt(integer).toString()

  return {
    coefficient,
    scale: rawFraction.length,
    normalized: `${negative && coefficient !== 0n ? '-' : ''}${normalizedMagnitude}`,
    canonical: canonicalDecimal(coefficient, rawFraction.length),
  }
}

function requiredFields(mode: RatioMode): RatioFieldKey[] {
  if (mode === 'solve') return ['a', 'b', 'c']
  if (mode === 'scale') return ['a', 'b', 'multiplier']
  return ['a', 'b']
}

function normalizedSnapshot(
  mode: RatioMode,
  inputs: RatioInputs,
  values: Partial<Record<RatioFieldKey, DecimalValue>>,
): string {
  return [
    mode,
    ...requiredFields(mode).map((field) => values[field]?.canonical ?? inputs[field].trim()),
  ].join('|')
}

function errorState(
  error: RatioCalculationError,
  invalidFields: RatioFieldKey[],
  mode: RatioMode,
  inputs: RatioInputs,
  values: Partial<Record<RatioFieldKey, DecimalValue>>,
): RatioCalculationState {
  return {
    status: 'error',
    error,
    invalidFields,
    result: null,
    snapshot: normalizedSnapshot(mode, inputs, values),
  }
}

interface FormattedRational {
  text: string
  rounded: boolean
}

function formatRational(numerator: bigint, denominator: bigint): FormattedRational {
  if (denominator === 0n) throw new Error('Denominator cannot be zero')

  const isNegative = (numerator < 0n) !== (denominator < 0n)
  const positiveNumerator = absolute(numerator)
  const positiveDenominator = absolute(denominator)
  const precisionFactor = powerOfTen(RESULT_DECIMAL_PLACES)
  const scaled = positiveNumerator * precisionFactor
  let roundedInteger = scaled / positiveDenominator
  const remainder = scaled % positiveDenominator
  const rounded = remainder !== 0n

  if (remainder * 2n >= positiveDenominator) roundedInteger += 1n

  const integerPart = roundedInteger / precisionFactor
  const fractionalPart = (roundedInteger % precisionFactor)
    .toString()
    .padStart(RESULT_DECIMAL_PLACES, '0')
    .replace(/0+$/, '')
  const magnitude = fractionalPart ? `${integerPart}.${fractionalPart}` : integerPart.toString()
  const hasNonZeroResult = roundedInteger !== 0n

  return {
    text: `${isNegative && hasNonZeroResult ? '-' : ''}${magnitude}`,
    rounded,
  }
}

function simplifyRatio(left: DecimalValue, right: DecimalValue): { left: bigint; right: bigint } {
  const commonScale = Math.max(left.scale, right.scale)
  let scaledLeft = left.coefficient * powerOfTen(commonScale - left.scale)
  let scaledRight = right.coefficient * powerOfTen(commonScale - right.scale)

  if (scaledRight < 0n) {
    scaledLeft = -scaledLeft
    scaledRight = -scaledRight
  }

  const divisor = greatestCommonDivisor(scaledLeft, scaledRight)
  return {
    left: scaledLeft / divisor,
    right: scaledRight / divisor,
  }
}

function readyState(
  mode: RatioMode,
  inputs: RatioInputs,
  values: Partial<Record<RatioFieldKey, DecimalValue>>,
  result: RatioCalculationResult,
): RatioCalculationState {
  return {
    status: 'ready',
    error: null,
    invalidFields: [],
    result,
    snapshot: normalizedSnapshot(mode, inputs, values),
  }
}

export function calculateRatioProportion(mode: RatioMode, inputs: RatioInputs): RatioCalculationState {
  const required = requiredFields(mode)
  const values: Partial<Record<RatioFieldKey, DecimalValue>> = {}

  for (const field of required) {
    const input = inputs[field]
    if (input.trim() === '') continue
    if (input.length > MAX_RATIO_INPUT_LENGTH) {
      return errorState('number_too_long', [field], mode, inputs, values)
    }

    const parsed = parseDecimal(input)
    if (!parsed) return errorState('invalid_number', [field], mode, inputs, values)
    values[field] = parsed
  }

  if (required.some((field) => inputs[field].trim() === '')) {
    return { status: 'incomplete', error: null, invalidFields: [], result: null, snapshot: null }
  }

  const a = values.a!
  const b = values.b!

  if (mode === 'solve') {
    if (a.coefficient === 0n) {
      return errorState('division_by_zero', ['a'], mode, inputs, values)
    }

    const c = values.c!
    const numerator = b.coefficient * c.coefficient * powerOfTen(a.scale)
    const denominator = a.coefficient * powerOfTen(b.scale + c.scale)
    const formatted = formatRational(numerator, denominator)

    return readyState(mode, inputs, values, {
      display: `X = ${formatted.text}`,
      formula: `X = (${b.normalized} × ${c.normalized}) ÷ ${a.normalized}`,
      detail: `${a.normalized}:${b.normalized} = ${c.normalized}:${formatted.text}`,
      rounded: formatted.rounded,
    })
  }

  if (a.coefficient === 0n && b.coefficient === 0n) {
    return errorState('undefined_ratio', ['a', 'b'], mode, inputs, values)
  }

  if (mode === 'simplify') {
    const simplified = simplifyRatio(a, b)
    const display = `${simplified.left}:${simplified.right}`

    return readyState(mode, inputs, values, {
      display,
      formula: `${a.normalized}:${b.normalized} = ${display}`,
      detail: 'Оба члена приведены к целым числам и разделены на общий НОД.',
      rounded: false,
    })
  }

  const multiplier = values.multiplier!
  if (multiplier.coefficient === 0n) {
    return errorState('zero_multiplier', ['multiplier'], mode, inputs, values)
  }

  const scaleDenominator = powerOfTen(a.scale + multiplier.scale)
  const left = formatRational(a.coefficient * multiplier.coefficient, scaleDenominator)
  const rightDenominator = powerOfTen(b.scale + multiplier.scale)
  const right = formatRational(b.coefficient * multiplier.coefficient, rightDenominator)

  return readyState(mode, inputs, values, {
    display: `${left.text}:${right.text}`,
    formula: `${a.normalized}:${b.normalized} × ${multiplier.normalized}`,
    detail: `${a.normalized} × ${multiplier.normalized} : ${b.normalized} × ${multiplier.normalized}`,
    rounded: left.rounded || right.rounded,
  })
}
